import { useCallback, useEffect, useRef, useState } from "react";
import { useCellSelection, type UseCellSelection } from "../lib/cellSelection";
import SelectionStatusBar from "./SelectionStatusBar";

// The per-grid half of Excel-style cell selection: the clipboard key handler, the
// "Copied" flash, the focus target, and the status bar element.
//
// useCellSelection owns the selection MATH (what is selected, what it foots to); this
// owns the WIRING every grid repeats around it. It exists because that wiring was
// copy-pasted between DataTable and ColumnFilterGrid, and adding it to the remaining
// grids would have pasted it three more times — five near-identical copies is where a
// fix lands in four of them (CLAUDE.md §7).
//
// Purely client-side over rows already in the browser: no API call, no re-finalize,
// zero added recon wall-clock (§11).

export interface UseGridSelection extends UseCellSelection {
  /** Spread onto the focusable wrapper directly inside which the table lives. */
  gridProps: {
    ref: React.MutableRefObject<HTMLDivElement | null>;
    tabIndex: number;
    onKeyDown: (e: React.KeyboardEvent) => void;
    className: string;
  };
  /** Call from a cell/header mousedown so Ctrl+C reaches THIS grid's handler. */
  focusGrid: () => void;
  /** The aggregate readout. Renders nothing while nothing is selected. */
  statusBar: React.ReactElement;
}

/**
 * @param rows    the FILTERED rows backing the grid, in selection-coordinate order
 * @param colIdx  visible-column position → index into a row array
 * @param labels  visible-column headers, for the TSV copy header line
 * @param rowIds  stable per-row identity parallel to `rows`; drives the stale-guard
 */
export function useGridSelection(
  rows: unknown[][],
  colIdx: number[],
  labels: string[],
  rowIds?: (string | number)[],
): UseGridSelection {
  const sel = useCellSelection(rows, colIdx, labels, rowIds);
  const [copied, setCopied] = useState(false);
  const gridRef = useRef<HTMLDivElement | null>(null);

  // Ctrl/Cmd+C copies the selection as TSV, but only while focus is inside THIS grid —
  // otherwise every grid on the page would race to answer one copy.
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "c") return;
    const tsv = sel.toTsv();
    if (!tsv) return;
    e.preventDefault();
    navigator.clipboard?.writeText(tsv).then(
      () => { setCopied(true); window.setTimeout(() => setCopied(false), 1600); },
      () => { /* clipboard blocked (insecure origin) — selection stays, no toast */ },
    );
  }, [sel]);

  useEffect(() => { setCopied(false); }, [sel.selection]);

  const focusGrid = useCallback(() => gridRef.current?.focus({ preventScroll: true }), []);

  return {
    ...sel,
    focusGrid,
    gridProps: { ref: gridRef, tabIndex: -1, onKeyDown, className: "grid-focus" },
    statusBar: <SelectionStatusBar aggregate={sel.aggregate} onClear={sel.clear} copied={copied} />,
  };
}
